import { CheckboxCard, Eyebrow, FieldSet, TextField } from "@/components/ui";
import { FormField } from "@/components/ui/Form";
import type { ModuleAssessment } from "@/types";

type RequirementKey = "isCapstone" | "isHurdle" | "mandatoryPass";

interface AssessmentRequirementControlsProps {
  assessment: ModuleAssessment;
  disabled?: boolean;
  /** Why every control here refuses, when it does. */
  disabledReason?: string;
  testIdPrefix: string;
  onChange: (
    field: RequirementKey | "minimumPassMark",
    value: boolean | number | undefined,
  ) => void;
}

const REQUIREMENTS: {
  key: RequirementKey;
  label: string;
  description: string;
}[] = [
  {
    key: "isCapstone",
    label: "Capstone assessment",
    description:
      "A culminating task that integrates substantial learning from the module or programme.",
  },
  {
    key: "isHurdle",
    label: "Hurdle requirement",
    description: "A non-compensatable requirement that must be satisfied independently.",
  },
  {
    key: "mandatoryPass",
    label: "Mandatory pass",
    description: "The learner must pass this assessment to pass the module.",
  },
];

export function AssessmentRequirementControls({
  assessment,
  disabled = false,
  disabledReason = undefined,
  testIdPrefix,
  onChange,
}: AssessmentRequirementControlsProps) {
  const showMinimumPassMark =
    assessment.isHurdle === true ||
    assessment.mandatoryPass === true ||
    assessment.minimumPassMark != null;

  return (
    <FieldSet legend={<Eyebrow as="span">Progression requirements</Eyebrow>} className="mt-5">
      <div className="grid gap-2 lg:grid-cols-3">
        {REQUIREMENTS.map(({ key, label, description }) => (
          <CheckboxCard
            key={key}
            id={`${testIdPrefix}-${key}`}
            label={label}
            description={description}
            checked={assessment[key] === true}
            onChange={(next) => onChange(key, next)}
            disabled={disabled}
            disabledReason={disabledReason}
            data-testid={`${testIdPrefix}-${key}`}
          />
        ))}
      </div>
      {showMinimumPassMark ? (
        <FormField
          label="Minimum pass mark %"
          htmlFor={`${testIdPrefix}-minimumPassMark`}
          helpText="Optional. Record the threshold where it differs from the normal module pass mark."
          className="mt-3 max-w-xs"
        >
          <TextField
            id={`${testIdPrefix}-minimumPassMark`}
            type="number"
            min={0}
            max={100}
            step={1}
            size="sm"
            className="w-full"
            value={assessment.minimumPassMark ?? ""}
            onChange={(next) =>
              onChange(
                "minimumPassMark",
                next ? Math.min(100, Math.max(0, Number(next))) : undefined,
              )
            }
            disabled={disabled}
            disabledReason={disabledReason}
            data-testid={`${testIdPrefix}-minimumPassMark`}
          />
        </FormField>
      ) : null}
    </FieldSet>
  );
}
